/**
 * EE Scheduler 前端服務（呼叫 /api/ee/scheduler/*）
 */

import axios from '../api/axios';
import { I18nField } from '../types';

const BASE = '/api/ee/scheduler';

// ============================================================
// Types
// ============================================================

export interface SchedulerJob {
  id: number;
  job_code: string;
  job_name: I18nField;
  description?: I18nField;
  cron_expr: string;
  function_code: string;
  params: Record<string, any>;
  job_type: 'exclusive' | 'repeatable';
  is_active: boolean;
  is_builtin: boolean;
  fail_alert_enabled: boolean;
  overdue_alert_enabled: boolean;
  status: 'waiting' | 'running';
  consecutive_fail_count: number;
  run_count: number;
  last_run_at: string | null;
  current_run_at: string | null;
  next_run_at: string | null;
  edit_by: number;
  created_at: string;
  updated_at: string;
}

export interface SchedulerJobCreate {
  job_code: string;
  job_name: I18nField;
  description?: I18nField;
  cron_expr: string;
  function_code: string;
  params?: Record<string, any>;
  job_type: 'exclusive' | 'repeatable';
  is_active?: boolean;
  fail_alert_enabled?: boolean;
  overdue_alert_enabled?: boolean;
}

export interface SchedulerJobUpdate {
  job_name?: I18nField;
  description?: I18nField;
  cron_expr?: string;
  function_code?: string;
  params?: Record<string, any>;
  job_type?: 'exclusive' | 'repeatable';
  is_active?: boolean;
  fail_alert_enabled?: boolean;
  overdue_alert_enabled?: boolean;
}

export interface SchedulerRun {
  id: number;
  job_id: number;
  job_code: string;
  queue_id: number | null;
  trigger_source: string;
  started_at: string;
  finished_at: string | null;
  duration_ms: number | null;
  status: 'running' | 'success' | 'failed';
  result: Record<string, any> | null;
  error_code: string | null;
  error_message: string | null;
  error_traceback: string | null;
  host: string | null;
}

export interface RunListResponse {
  total: number;
  page: number;
  page_size: number;
  items: SchedulerRun[];
}

export interface QueueItem {
  id: number;
  job_id: number;
  job_code: string;
  scheduled_at: string;
  enqueued_at: string;
  dequeued_at: string | null;
  status: 'pending' | 'dispatched' | 'done';
  run_id: number | null;
}

export interface QueueStats {
  pending_count: number;
  dispatched_count: number;
  oldest_pending_at: string | null;
}

export interface SchedulerStatus {
  running: boolean;
  watchdog_alive: boolean;
  trigger_alive: boolean;
  backend: string;
  phase: string;
}

// ============================================================
// API
// ============================================================

export const listJobs = async (params?: {
  is_active?: boolean;
  job_type?: 'exclusive' | 'repeatable';
  status?: 'waiting' | 'running';
}): Promise<SchedulerJob[]> => {
  const res = await axios.get(`${BASE}/jobs`, { params });
  return res.data;
};

export const getJob = async (id: number): Promise<SchedulerJob> => {
  const res = await axios.get(`${BASE}/jobs/${id}`);
  return res.data;
};

export const createJob = async (payload: SchedulerJobCreate): Promise<SchedulerJob> => {
  const res = await axios.post(`${BASE}/jobs`, payload);
  return res.data;
};

export const updateJob = async (id: number, payload: SchedulerJobUpdate): Promise<SchedulerJob> => {
  const res = await axios.put(`${BASE}/jobs/${id}`, payload);
  return res.data;
};

export const deleteJob = async (id: number): Promise<void> => {
  await axios.delete(`${BASE}/jobs/${id}`);
};

export const toggleJob = async (id: number): Promise<SchedulerJob> => {
  const res = await axios.post(`${BASE}/jobs/${id}/toggle`);
  return res.data;
};

export const runJobNow = async (id: number): Promise<{ queue_id: number }> => {
  const res = await axios.post(`${BASE}/jobs/${id}/run`);
  return res.data;
};

export const previewCron = async (cron_expr: string, count = 5): Promise<{
  cron_expr: string;
  next_runs: string[];
}> => {
  const res = await axios.post(`${BASE}/jobs/preview`, { cron_expr, count });
  return res.data;
};

export const listRuns = async (params?: {
  job_id?: number;
  status?: 'running' | 'success' | 'failed';
  started_from?: string;
  started_to?: string;
  page?: number;
  page_size?: number;
}): Promise<RunListResponse> => {
  const res = await axios.get(`${BASE}/runs`, { params });
  return res.data;
};

export const getRun = async (id: number): Promise<SchedulerRun> => {
  const res = await axios.get(`${BASE}/runs/${id}`);
  return res.data;
};

export const listQueue = async (): Promise<QueueItem[]> => {
  const res = await axios.get(`${BASE}/queue`);
  return res.data;
};

export const getQueueStats = async (): Promise<QueueStats> => {
  const res = await axios.get(`${BASE}/queue/stats`);
  return res.data;
};

export const getStatus = async (): Promise<SchedulerStatus> => {
  const res = await axios.get(`${BASE}/status`);
  return res.data;
};
